import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt, faGlasses, faBookOpen, faGraduationCap, faLock } from "@fortawesome/free-solid-svg-icons";
import TurtleIcon from "../AssistantWidget/TurtleIcon";
import { Achievement, AchievementIcon } from "../../utils/achievements";
import styles from "./achievementBadge.module.scss";

const ACCESSORY_ICONS: Record<AchievementIcon, typeof faPencilAlt> = {
    pencil: faPencilAlt,
    glasses: faGlasses,
    book: faBookOpen,
    cap: faGraduationCap,
};

// At the small sizes these badges render at (as little as ~28px, e.g. the
// homepage row), the accessory glyph alone is too small to read apart from
// the others - a distinct color per achievement makes them tell apart at a
// glance even when the icon shape itself is illegible.
const ACCESSORY_COLORS: Record<AchievementIcon, string> = {
    pencil: "#d7263d",
    glasses: "#1d6fd6",
    book: "#7c3aed",
    cap: "#b45309",
};

type AchievementBadgeProps = {
    achievement: Achievement;
    // Locked badges are shown too (in the profile's full list) so a learner
    // can see what's still to earn - dimmed, with a lock instead of the
    // achievement's own accessory icon.
    locked?: boolean;
    size?: number;
};

// A round かめ (turtle) badge for one achievement - the same mascot used
// everywhere else in the app (header logo, かめ assistant), so an unlocked
// achievement reads as unmistakably part of KanaLearn rather than a generic
// trophy icon. The small accessory icon in the corner (pencil, glasses,
// book, cap) is what actually distinguishes one achievement from another.
function AchievementBadge({ achievement, locked = false, size = 48 }: AchievementBadgeProps) {
    const accessoryIcon = locked ? faLock : ACCESSORY_ICONS[achievement.icon];
    const accessorySize = Math.max(size * 0.5, 16);
    const label = `${achievement.title}: ${achievement.description}${locked ? " (locked)" : ""}`;

    return (
        <div
            className={locked ? `${styles.badge} ${styles.badgeLocked}` : styles.badge}
            style={{ width: size, height: size }}
            role="img"
            aria-label={label}
            title={label}
        >
            <TurtleIcon size={size} className={styles.badgeTurtle} animated={false} />
            <span
                className={styles.badgeAccessory}
                style={{
                    width: accessorySize,
                    height: accessorySize,
                    fontSize: accessorySize * 0.58,
                    color: locked ? undefined : ACCESSORY_COLORS[achievement.icon],
                }}
            >
                <FontAwesomeIcon icon={accessoryIcon} />
            </span>
        </div>
    );
}

export default AchievementBadge;
